import { FormGroup, Select as GelSelect } from '@snsw/react-component-library';
import { ChangeEvent, ForwardedRef, forwardRef, ReactNode, useMemo } from 'react';
import { useLatest } from 'react-use';
import { LookupValue, PropsWithForwardedRef } from '../../../types/types';
import { extractKey } from '../ObjectWithId';

export interface SelectProps<TValue> {
  id?: string;
  name: string;
  label: string;
  helpMessage?: ReactNode;
  className?: string;
  value?: TValue;
  onChange: (next: TValue, event: ChangeEvent<HTMLSelectElement>) => void;
  options: LookupValue<TValue>[];
  getOptionKey?: (value: TValue) => string;
  errorMessage?: string;
  disabled?: boolean;
  required?: boolean;
  optional?: boolean;
  placeholder?: string;
  inputWidth?: 'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl';
}

const useSelectOptions = <TValue extends unknown>(
  options: SelectProps<TValue>['options'],
  getOptionKey: SelectProps<TValue>['getOptionKey']
) => {
  const getOptionKeyRef = useLatest(getOptionKey);

  return useMemo(() => {
    const getValue = getOptionKeyRef.current ?? extractKey;

    return options.map(({ label, value }) => ({
      text: label,
      value: getValue(value),
    }));
  }, [options]);
};

const SelectWithRef = <TValue extends unknown>(
  {
    id,
    name,
    label,
    errorMessage,
    helpMessage,
    required,
    optional,
    className,
    value,
    onChange,
    options,
    getOptionKey,
    ...selectProps
  }: SelectProps<TValue>,
  ref?: ForwardedRef<HTMLSelectElement>
) => {
  const getKey = getOptionKey ?? extractKey;

  const selectedValue = getKey(value);
  const selectOptions = useSelectOptions(options, getOptionKey);

  const handleChange = (event: ChangeEvent<HTMLSelectElement>) => {
    if (!onChange) return;

    const selectedOption = options.find((o) => getKey(o.value) == event.target.value);
    onChange && onChange(selectedOption?.value || (event.target.value as TValue), event);
  };

  return (
    <FormGroup
      id={`${id || name}-form-group`}
      hasError={!!errorMessage}
      errorMessage={errorMessage}
      helpMessage={helpMessage}
      label={label}
      isRequired={required}
      isOptional={optional}
      className={className}
    >
      <GelSelect
        id={id}
        name={name}
        value={selectedValue}
        options={selectOptions}
        onChange={handleChange}
        inputRef={ref}
        {...selectProps}
      />
    </FormGroup>
  );
};

export const Select = forwardRef(SelectWithRef) as <TValue extends unknown>(
  props: PropsWithForwardedRef<SelectProps<TValue>, HTMLSelectElement>
) => ReturnType<typeof SelectWithRef>;
